import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Button,
  Modal,
  LinearProgress,
  Chip
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface HubSpotDeal {
  id: string;
  properties: {
    dealname?: string;
    pipeline?: string;
    pipelineName?: string;
    dealstage?: string;
    dealStageName?: string;
    dealtype?: string;
    amount?: string;
    primary_property?: string;
    [key: string]: string | undefined;
  };
}

interface HubSpotListing {
  id: string;
  properties: {
    hs_name?: string;
    hs_address_1?: string;
    hs_city?: string;
    hs_state_province?: string;
    hs_zip?: string;
    latitude?: string;
    longitude?: string;
    location?: string;
    property_hotel_id?: string;
    str_id?: string;
    rooms?: string;
    chain_scale?: string;
    market?: string;
    country?: string;
    [key: string]: string | undefined;
  };
}

export const NewAppraisalFromDealPage: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Deal and listing state
  const [deal, setDeal] = useState<HubSpotDeal | null>(null);
  const [listing, setListing] = useState<HubSpotListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Orchestration state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [operationProgress, setOperationProgress] = useState<{ 
    step?: string; 
    percent?: number; 
    status?: string; 
    error_message?: string; 
    progress_text?: string 
  } | null>(null);
  const pollRef = React.useRef<any>(null);

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    event.preventDefault();
    navigate(path);
  };

  // Fetch HubSpot deal and associated listing
  const fetchDealAndListing = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch the deal
      const dealResponse = await authenticatedFetch(
        `${API_URL}/api/hubspot/deals/${dealId}?properties=dealname,pipeline,dealstage,dealtype,amount,primary_property`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!dealResponse) {
        throw new Error('No response from server');
      }

      if (!dealResponse.ok) {
        const errorData = await dealResponse.json();
        throw new Error(errorData.message || `Error: ${dealResponse.status}`);
      }

      const dealResult = await dealResponse.json();
      if (dealResult.success && dealResult.data) {
        setDeal(dealResult.data);
      } else {
        throw new Error(dealResult.message || 'Failed to fetch HubSpot deal');
      }

      // Fetch associated listings
      const associationsResponse = await authenticatedFetch(
        `${API_URL}/api/hubspot/deals/${dealId}/associations/listings`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (associationsResponse && associationsResponse.ok) {
        const associationsResult = await associationsResponse.json();
        console.log('Associations response:', associationsResult);
        // Handle both formats: data as array or data.results as array
        const associations = associationsResult.data?.results || associationsResult.data;
        if (associationsResult.success && associations && Array.isArray(associations) && associations.length > 0) {
          // Get the first listing ID
          const listingId = associations[0].toObjectId || associations[0].id;
          console.log('Found listing ID:', listingId);
          
          // Fetch listing details
          const listingResponse = await authenticatedFetch(
            `${API_URL}/api/hubspot/listings/${listingId}?properties=hs_name,hs_address_1,hs_city,hs_state_province,hs_zip,latitude,longitude,location,property_hotel_id,str_id,rooms,chain_scale,market,country`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (listingResponse && listingResponse.ok) {
            const listingResult = await listingResponse.json();
            console.log('Listing details response:', listingResult);
            if (listingResult.success && listingResult.data) {
              setListing(listingResult.data);
            }
          } else {
            console.error('Failed to fetch listing details');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching deal and listing:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch deal information');
    } finally {
      setLoading(false);
    }
  }, [dealId, authenticatedFetch]);

  useEffect(() => {
    if (dealId) {
      fetchDealAndListing();
    }
  }, [dealId, fetchDealAndListing]);

  const onCreateAppraisal = async () => {
    if (!dealId) {
      setCreateError('No deal ID provided');
      return;
    }

    setCreateError(null);
    setCreating(true);

    try {
      const body = {
        hubspotDealId: dealId
      };

      const res = await authenticatedFetch(`${API_URL}/api/appraisal/create-from-deal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res || !res.ok) {
        const err = await res?.json().catch(() => ({} as any));
        throw new Error(err?.message || 'Failed to start appraisal creation');
      }

      const json = await res.json();
      const opId = json?.operationId as string | undefined;
      if (!opId) throw new Error('Missing operationId');
      setOperationId(opId);

      // Begin polling operations status every 2.5s
      const poll = async () => {
        try {
          const resp = await authenticatedFetch(`${API_URL}/api/operations/${opId}`, { method: 'GET' });
          if (!resp || !resp.ok) return; // keep polling
          const data = await resp.json();
          const op = data?.operation;
          if (op) {
            setOperationProgress({ 
              step: op?.progress?.step, 
              percent: op?.progress?.percent, 
              status: op?.status, 
              error_message: op?.error_message, 
              progress_text: op?.progress?.progress_text || op?.progress_text 
            });
            if (op.status === 'completed') {
              if (pollRef.current) clearInterval(pollRef.current);
              const appraisalId = op?.progress?.result?.appraisalId;
              if (appraisalId) navigate(`/appraisals/${appraisalId}/edit`);
              setCreating(false);
            } else if (op.status === 'error' || op.status === 'failed') {
              if (pollRef.current) clearInterval(pollRef.current);
              const appraisalId = op?.progress?.result?.appraisalId;
              if (appraisalId) {
                navigate(`/appraisals/${appraisalId}/creation-error/${op.id}`);
              } else {
                navigate(`/appraisals/creation-error/${op.id}`);
              }
              setCreating(false);
            }
          }
        } catch {}
      };
      poll();
      pollRef.current = setInterval(poll, 2500);
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to start');
      setCreating(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const formatAmount = (amount?: string) => {
    if (!amount) return '-';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getDealStageColor = (stage?: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    if (!stage) return 'default';
    const lowerStage = stage.toLowerCase();
    if (lowerStage.includes('won') || lowerStage.includes('closed')) return 'success';
    if (lowerStage.includes('qualified')) return 'info';
    if (lowerStage.includes('proposal') || lowerStage.includes('presentation')) return 'warning';
    if (lowerStage.includes('lost')) return 'error';
    return 'default';
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            color="inherit"
            href="/"
            onClick={(e) => handleBreadcrumbClick(e, '/')}
            sx={{ 
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Home
          </Link>
          <Link
            color="inherit"
            href="/appraisals"
            onClick={(e) => handleBreadcrumbClick(e, '/appraisals')}
            sx={{ 
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Appraisals
          </Link>
          <Link
            color="inherit"
            href="/appraisals/new"
            onClick={(e) => handleBreadcrumbClick(e, '/appraisals/new')}
            sx={{ 
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            New Appraisal
          </Link>
          <Typography color="text.primary">From Deal</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Create Appraisal from HubSpot Deal
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Review deal and listing information before creating the appraisal
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <>
            {/* Action Panel */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Create Appraisal</Typography>
              {createError && (
                <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
              )}
              {!listing ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No listing is associated with this deal. A listing is required to find the matching property.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The system will search for a matching property in the market analytics database using the listing information.
                  If a match is found, an appraisal will be created.
                </Alert>
              )}
              <Button 
                variant="contained" 
                color="primary" 
                onClick={onCreateAppraisal} 
                disabled={creating || !listing}
              >
                {creating ? 'Creating…' : 'Create Appraisal'}
              </Button>
            </Paper>

            {/* Deal Information */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>HubSpot Deal</Typography>
              {!deal ? (
                <Typography variant="body2" color="text.secondary">No deal data</Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Deal ID</Typography>
                    <Typography variant="body1">{deal.id}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Deal Name</Typography>
                    <Typography variant="body1">{deal.properties.dealname || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Pipeline</Typography>
                    <Typography variant="body1">
                      {deal.properties.pipelineName || deal.properties.pipeline || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Stage</Typography>
                    {deal.properties.dealstage ? (
                      <Chip 
                        label={deal.properties.dealStageName || deal.properties.dealstage}
                        size="small"
                        color={getDealStageColor(deal.properties.dealStageName || deal.properties.dealstage)}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body1">-</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                    <Typography variant="body1">{deal.properties.dealtype || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: deal.properties.amount ? 500 : 400 }}>
                      {formatAmount(deal.properties.amount)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>

            {/* Listing Information */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Associated Listing</Typography>
              {!listing ? (
                <Typography variant="body2" color="text.secondary">
                  No listing associated with this deal
                </Typography>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Property Name</Typography>
                      <Typography variant="body1">{listing.properties.hs_name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">STR Number</Typography>
                      <Typography variant="body1">{listing.properties.str_id || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">CoStar Property ID</Typography>
                      <Typography variant="body1">{listing.properties.property_hotel_id || '-'}</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">
                        {[
                          listing.properties.hs_address_1,
                          listing.properties.hs_city,
                          listing.properties.hs_state_province,
                          listing.properties.hs_zip
                        ].filter(Boolean).join(', ') || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Latitude</Typography>
                      <Typography variant="body1">{listing.properties.latitude || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Longitude</Typography>
                      <Typography variant="body1">{listing.properties.longitude || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                      <Typography variant="body1">{listing.properties.country || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Market</Typography>
                      <Typography variant="body1">{listing.properties.market || '-'}</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Rooms</Typography>
                      <Typography variant="body1">{listing.properties.rooms || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Chain Scale</Typography>
                      <Typography variant="body1">{listing.properties.chain_scale || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Location Type</Typography>
                      <Typography variant="body1">{listing.properties.location || '-'}</Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* Blocking overlay while creating */}
      <Modal
        open={creating}
        aria-labelledby="creating-appraisal-title"
        aria-describedby="creating-appraisal-description"
        disableEscapeKeyDown
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.6)' } } }}
      >
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          width: 'min(560px, 90vw)', 
          bgcolor: 'background.paper', 
          borderRadius: 2, 
          boxShadow: 24, 
          p: 4, 
          outline: 0 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={28} />
            <Typography id="creating-appraisal-title" variant="h6">Creating Appraisal</Typography>
          </Box>
          <Typography id="creating-appraisal-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {operationProgress?.progress_text || operationProgress?.step || 'Starting…'}
          </Typography>
          {typeof operationProgress?.percent === 'number' ? (
            <LinearProgress variant="determinate" value={operationProgress.percent} />
          ) : (
            <LinearProgress />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default NewAppraisalFromDealPage;